/** @file Atlas for a TextureLayout: identity pages (one per image, full rect) or the packed atlas. */
import { entriesByName } from '@pose2d/format/doc/entriesByName';
import { throwError } from '@pose2d/format/throwError';
import type { AtlasJson, Doc } from '@pose2d/format/types';
import type { TextureLayout } from '../textureLayout';

/** Every attachment image must have a region in a packed atlas (VALIDATION at the attachment otherwise). */
function checkPacked(doc: Doc, atlas: AtlasJson): void {
  for (const [id, att] of entriesByName(doc.attachments)) {
    const name = doc.images[att.image]?.name;
    if (name !== undefined && !Object.hasOwn(atlas.regions, name)) {
      throwError('VALIDATION', undefined, { path: `attachments.${id}.image`, hint: `image "${name}" has no region in the packed atlas` });
    }
  }
}

/**
 * One page per image in image name order (R12-01) (§5.11, W2-16, R2-08): unused images are kept (AR-7 only warns),
 * no sha256 dedup; file `<sha256>.png`, untrimmed; regions keyed by image name.
 */
function identityAtlas(doc: Doc): AtlasJson {
  const atlas: AtlasJson = { pages: [], regions: {} };
  for (const [, image] of entriesByName(doc.images)) {
    const page = atlas.pages.length;
    atlas.pages.push({ file: `${image.sha256}.png`, w: image.w, h: image.h, pma: false, filter: 'linear' });
    atlas.regions[image.name] = { page, x: 0, y: 0, w: image.w, h: image.h, rotated: false, origW: image.w, origH: image.h, offL: 0, offB: 0 };
  }
  return atlas;
}

/** The atlas the compiled rig's attachments resolve against (§5.11; §6.14 texture identity). */
export function compileAtlas(doc: Doc, layout: TextureLayout): AtlasJson {
  if (layout.kind === 'identity') return identityAtlas(doc);
  checkPacked(doc, layout.atlas);
  return layout.atlas;
}
